import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQontoStatus, useQontoTrennen, useQontoVerbinden, useQontoWebhook } from "../hooks/useLastschrift";
import { fehlertext } from "../lib/fehler";
import "../styles/lastschrift.css";

/**
 * Qonto-Verbindung der Pflege. Steht seit 09.10.2026 unter Einstellungen statt
 * auf der Zahlungsseite: man stellt sie einmal ein, man arbeitet nicht damit.
 * Entfällt mit Etappe 8 des Plans (Qonto aus der Pflege entfernen).
 */
/** Rückmeldungen, mit denen der OAuth-Rückläufer hierher zurückleitet. */
const RUECKMELDUNG: Record<string, { gut: boolean; text: string }> = {
  verbunden: { gut: true, text: "Qonto ist verbunden. Lastschriften der Pflegeverträge laufen ab jetzt über Qonto." },
  "verbunden-ohne-webhook": {
    gut: true,
    text: "Qonto ist verbunden. Rückmeldungen holt der Server einmal täglich selbst ab (Qonto hat das Sofort-Abo nicht angenommen).",
  },
  abgebrochen: { gut: false, text: "Die Verbindung wurde bei Qonto abgebrochen." },
  fehler: { gut: false, text: "Qonto hat die Anmeldung abgelehnt. Stimmt die Redirect-URI in der Qonto-App (Production)?" },
  "ohne-refresh": {
    gut: false,
    text: "Qonto hat keine dauerhafte Freigabe erteilt. In der Qonto-App den Scope offline_access aktivieren und neu verbinden.",
  },
};

const tag = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");

export default function QontoZugang() {
  const [params, setParams] = useSearchParams();
  const status = useQontoStatus();
  const verbinden = useQontoVerbinden();
  const webhook = useQontoWebhook();
  const trennen = useQontoTrennen();
  const [trennenArmed, setTrennenArmed] = useState(false);

  const rueck = RUECKMELDUNG[params.get("qonto") || ""];
  useEffect(() => {
    if (!rueck) return;
    status.refetch();
    const t = setTimeout(() => {
      params.delete("qonto");
      setParams(params, { replace: true });
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rueck]);

  const s = status.data;
  return (
    <div className="pf-card ls-karte">
      <div className="ls-kopf">
        <span className="ls-titel">Qonto-Konto</span>
        {s && (
          <span className={`ls-marke ${s.verbunden ? "ls-gut" : "ls-offen"}`}>
            {s.verbunden ? "Verbunden" : "Nicht verbunden"}
          </span>
        )}
      </div>
      {rueck && <div className={`ls-banner ${rueck.gut ? "ls-gut" : "ls-rot"}`}>{rueck.text}</div>}

      {status.isPending ? (
        <p className="ls-text">Lade…</p>
      ) : status.isError ? (
        <p className="ls-fehler">{fehlertext(status.error)}</p>
      ) : s?.verbunden ? (
        <>
          <p className="ls-text">
            Mandate entstehen beim Vertragsabschluss bei Qonto, fällige Raten zieht der Server jeden Morgen um
            06:10 ein. Verbunden am {tag(s.verbunden_am)}, zuletzt erneuert {tag(s.erneuert_am)} ·{" "}
            {s.webhook ? "Rückmeldungen sofort" : "Rückmeldungen einmal täglich"}.
          </p>
          <div className="ls-knoepfe">
            {!s.webhook && (
              <button type="button" className="pf-btn is-ghost ls-knopf" disabled={webhook.isPending}
                onClick={() => webhook.mutate()}>
                {webhook.isPending ? "Versucht…" : "Sofort-Rückmeldung einrichten"}
              </button>
            )}
            {!trennenArmed ? (
              <button type="button" className="pf-btn is-ghost ls-knopf" onClick={() => setTrennenArmed(true)}>
                Verbindung lösen
              </button>
            ) : (
              <>
                <span className="ls-text ls-frage">Sicher? Danach wird nichts mehr eingezogen, bis jemand neu verbindet.</span>
                <button type="button" className="pf-btn ls-knopf" disabled={trennen.isPending}
                  onClick={() => trennen.mutate(undefined, { onSuccess: () => setTrennenArmed(false) })}>
                  {trennen.isPending ? "Löst…" : "Ja, lösen"}
                </button>
                <button type="button" className="pf-btn is-ghost ls-knopf" onClick={() => setTrennenArmed(false)}>
                  Abbrechen
                </button>
              </>
            )}
          </div>
          {webhook.isSuccess && !webhook.data?.webhook && (
            <p className="ls-text">Qonto hat das Abo wieder nicht angenommen — der tägliche Abgleich genügt.</p>
          )}
          {webhook.error && <p className="ls-fehler">{fehlertext(webhook.error)}</p>}
        </>
      ) : (
        <>
          {s?.hinweis && <p className="ls-fehler">{s.hinweis}</p>}
          <p className="ls-text">
            Verbinde das Qonto-Konto der GbR. Danach bestätigen Kunden die Lastschrift ihres Pflegevertrags direkt bei
            Qonto, und die Raten werden von selbst eingezogen. Ohne Verbindung fragt die Kundenseite wie bisher nach der
            IBAN (Einzug per Datei über die Sparkasse).
          </p>
          {s?.app_eingerichtet ? (
            <button type="button" className="pf-btn ls-knopf" disabled={verbinden.isPending}
              onClick={() => verbinden.mutate()}>
              {verbinden.isPending ? "Öffnet Qonto…" : "Mit Qonto verbinden"}
            </button>
          ) : (
            <p className="ls-text">
              Die Qonto-App ist auf dem Server noch nicht eingetragen (Client-ID und -Secret aus dem Developer Portal,
              Ansicht „Production“). Redirect-URI dort:
              <span className="ls-link">{s?.weiterleitung}</span>
            </p>
          )}
          {verbinden.error && <p className="ls-fehler">{fehlertext(verbinden.error)}</p>}
        </>
      )}
    </div>
  );
}

